import React from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiTrendingUp, FiZap, FiCheckCircle, FiBarChart2, FiUsers } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Hero = () => {
  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-slate-50">
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-emerald-200/40 rounded-full blur-[120px] -mr-64 -mt-64 opacity-60 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-200/40 rounded-full blur-[120px] -ml-48 -mb-48 opacity-60 pointer-events-none" />
      
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#cbd5e1_1px,transparent_1px),linear-gradient(to_bottom,#cbd5e1_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-flex items-center space-x-2 py-2 px-4 rounded-full bg-emerald-100 text-emerald-700 text-xs font-black mb-8 uppercase tracking-[0.2em] border border-emerald-200 shadow-sm">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              <span>Premium Growth Studio</span>
            </span>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 tracking-tight mb-6 leading-[1.1]">
              Turn Clicks Into <br className="hidden lg:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-blue-600">Paying Clients</span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-10 leading-relaxed font-medium max-w-xl">
              We engineer high-converting websites and automated growth systems that scale your business while you sleep.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href="https://go.digitalgigs.eu/chat" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-full sm:w-auto flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-emerald-600 hover:bg-emerald-700 transition-all shadow-[0_0_40px_rgba(16,185,129,0.3)] hover:shadow-[0_0_60px_rgba(16,185,129,0.4)] hover:-translate-y-1 active:scale-95 group"
              >
                Start Scaling Now
                <SafeIcon icon={FiArrowRight} className="ml-2 transition-transform group-hover:translate-x-1" />
              </a>
              <a 
                href="#projects" 
                className="w-full sm:w-auto px-8 py-4 text-lg font-bold rounded-full text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-all flex items-center justify-center hover:-translate-y-1 active:scale-95 shadow-sm"
              >
                View Our Work
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center sm:justify-start gap-6 text-sm font-bold text-slate-500">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-emerald-500 text-lg" />
                <span>Custom Design</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-emerald-500 text-lg" />
                <span>Smart Automation</span>
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-emerald-500 text-lg" />
                <span>SEO Optimized</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative hidden lg:block h-[500px] xl:h-[600px]"
          >
            <motion.div 
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] xl:w-[420px] bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 xl:p-8 z-20"
            >
              <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600">
                    <SafeIcon icon={FiTrendingUp} className="text-2xl" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Revenue Generated</p>
                    <p className="text-3xl font-black text-slate-900">$84,294.00</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {[
                  { name: "New Lead Captured", source: "Automated Workflow", time: "Just now", icon: FiUsers },
                  { name: "Meeting Scheduled", source: "Booking Funnel", time: "5m ago", icon: FiCheckCircle },
                  { name: "Payment Received", source: "Sales Page", time: "12m ago", icon: FiTrendingUp }
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100 cursor-default">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-500">
                        <SafeIcon icon={item.icon} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">{item.name}</p>
                        <p className="text-xs text-slate-500 font-medium">{item.source}</p>
                      </div>
                    </div>
                    <span className="text-emerald-500 font-bold text-xs">{item.time}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              animate={{ y: [0, 15, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 1 }}
              className="absolute top-12 -right-4 xl:right-0 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-4 z-30"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600">
                <SafeIcon icon={FiZap} className="text-xl" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">System Active</p>
                <p className="text-xs text-slate-500 font-medium">Running 24/7</p>
              </div>
            </motion.div>

            <motion.div 
              animate={{ y: [0, -20, 0] }}
              transition={{ repeat: Infinity, duration: 7, ease: "easeInOut", delay: 2 }}
              className="absolute bottom-16 -left-4 xl:left-0 bg-slate-900 p-5 rounded-2xl shadow-2xl flex items-center gap-4 z-30 border border-slate-800"
            >
              <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400">
                <SafeIcon icon={FiBarChart2} className="text-xl" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-300">Conversion Rate</p>
                <p className="text-xl font-black text-emerald-400 flex items-center gap-2">
                  12.4% <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">+2.1%</span>
                </p>
              </div>
            </motion.div>

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[450px] xl:w-[550px] h-[450px] xl:h-[550px] border border-slate-200 rounded-full opacity-50" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] xl:w-[400px] h-[300px] xl:h-[400px] border border-slate-200 rounded-full opacity-50" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
